import { useState } from "react";
import { Alert, Button, Card, Descriptions, Empty, Input, Result, Spin, Table, Tag } from "@arco-design/web-react";
import type { ColumnProps } from "@arco-design/web-react/es/Table";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { FINANCE_PAYMENT_REQUEST_CREATE_PERMISSION } from "../config";
import { useFinancePayableListRuntime, useFinancePayablePrepaymentSummary } from "../runtime";
import {
  financePayableAgreementStatusLabel,
  financePayableRequestBlockedReason,
  financePayableServiceProviderTypeLabel,
  financePayableWorkspaceStatusLabel,
  formatFinancePayableAmount,
  formatFinancePayableCount,
  formatFinancePayableServiceDates,
  hasFinancePayableUnattributedAmount,
  isFinancePayableItineraryNotFound,
  isFinancePayableSummaryEmpty,
  isFinancePayableWorkspaceNotFound,
} from "../service";
import type {
  FinancePayableItinerarySearchItem,
  FinancePayableLine,
  FinancePayablePrepaymentRow,
  FinancePayableRequestTotals,
  FinancePayableSettlementRow,
  FinancePayableSettlementSummary,
  FinancePayableUnassignedGroup,
  FinancePaymentRequestTarget,
  FinancePrepaymentItinerary,
  FinancePrepaymentTarget,
} from "../types";
import { FinancePaymentRequestFormModal, FinancePrepaymentFormModal } from "./FinancePaymentRequestFormModal";
import "./financePayable.css";

type Props = {
  itineraryId: string | null;
  onSelectItinerary: (itineraryId: string) => void;
};

/** 「按结算主体」卡片右上的入口名。 */
const PREPAYMENT_ENTRY = "给其他结算主体发起预付款";

/**
 * 应付清单页主体：顶部搜团，下方是选中团按结算主体一行的应付与团合计；能发起请款的人每行多一个「请款」，
 * 还有「给其他结算主体发起预付款」入口（团还没有确认件时也有）。能发起请款的人按线路名称搜不到时再按团编码找。
 */
export function FinancePayableListFeaturePage({ itineraryId, onSelectItinerary }: Props) {
  const canRequest = canUsePermission(useCurrentAuthUser().data, FINANCE_PAYMENT_REQUEST_CREATE_PERMISSION);
  const runtime = useFinancePayableListRuntime(itineraryId, canRequest);
  const selectItinerary = (id: string) => {
    runtime.closeSearch();
    onSelectItinerary(id);
  };

  return (
    <main className="finance-payable-page">
      <Card className="finance-payable-card">
        <div className="finance-payable-search-bar">
          <Input
            allowClear
            placeholder={canRequest ? "输入线路名称关键字或团编码" : "输入线路名称关键字"}
            value={runtime.keyword}
            onChange={runtime.setKeyword}
            onPressEnter={runtime.submitSearch}
          />
          <Button type="primary" onClick={runtime.submitSearch}>
            搜索
          </Button>
        </div>
        {runtime.searchOpen ? (
          <SearchResults
            search={runtime.search}
            teamLocate={runtime.teamLocateEnabled ? runtime.teamLocate : null}
            onSelect={selectItinerary}
          />
        ) : null}
      </Card>
      {itineraryId ? (
        <SettlementSummary itineraryId={itineraryId} summary={runtime.summary} canRequest={canRequest} />
      ) : (
        <Card className="finance-payable-card">
          <Empty description="按线路名称搜索并选择一个团，查看它的应付。" />
        </Card>
      )}
    </main>
  );
}

type SearchState = ReturnType<typeof useFinancePayableListRuntime>["search"];

type TeamLocateState = ReturnType<typeof useFinancePayableListRuntime>["teamLocate"];

/** 搜团结果：点线路标题选中这个团；确认件里搜不到时换成按团编码找到的团（teamLocate 为空表示没去找）。 */
function SearchResults({
  search,
  teamLocate,
  onSelect,
}: {
  search: SearchState;
  teamLocate: TeamLocateState | null;
  onSelect: (itineraryId: string) => void;
}) {
  if (search.isPending) return <Spin className="finance-payable-search-state" tip="正在搜索…" />;
  if (search.isError) {
    return (
      <Alert
        className="finance-payable-search-state"
        type="error"
        content="搜索失败，请稍后重试。"
        action={<Button size="mini" onClick={() => void search.refetch()}>重新搜索</Button>}
      />
    );
  }
  const { items, total } = search.data;
  if (items.length === 0) return <TeamLocateResults teamLocate={teamLocate} onSelect={onSelect} />;

  const columns: ColumnProps<FinancePayableItinerarySearchItem>[] = [
    {
      title: "线路标题",
      dataIndex: "itineraryTitle",
      render: (_, item) => (
        <Button type="text" className="finance-payable-link" onClick={() => onSelect(item.itineraryId)}>
          {item.itineraryTitle}
        </Button>
      ),
    },
    {
      title: "确认件状态",
      dataIndex: "workspaceStatus",
      width: 120,
      render: (_, item) => financePayableWorkspaceStatusLabel(item.workspaceStatus),
    },
    { title: "协议数", dataIndex: "agreementCount", width: 100, align: "right" },
    { title: "未归口行数", dataIndex: "unassignedLineCount", width: 110, align: "right" },
  ];
  return (
    <div className="finance-payable-search-state">
      <Table<FinancePayableItinerarySearchItem> rowKey="itineraryId" columns={columns} data={items} pagination={false} />
      {total > items.length ? (
        <p className="finance-payable-note">
          共 {total} 个结果，只显示最近更新的 {items.length} 个，请输入更具体的线路名称。
        </p>
      ) : null}
    </div>
  );
}

/** 按团编码找到的团（只有已签约、有团编码的团找得到）：点线路标题打开这个团。 */
function TeamLocateResults({
  teamLocate,
  onSelect,
}: {
  teamLocate: TeamLocateState | null;
  onSelect: (itineraryId: string) => void;
}) {
  if (teamLocate === null) return <Empty className="finance-payable-search-state" description="没有找到匹配的团" />;
  if (teamLocate.isPending) return <Spin className="finance-payable-search-state" tip="确认件里没有，正在按团编码找…" />;
  if (teamLocate.isError) {
    return (
      <Alert
        className="finance-payable-search-state"
        type="error"
        content="按团编码找团失败，请稍后重试。"
        action={<Button size="mini" onClick={() => void teamLocate.refetch()}>重新查找</Button>}
      />
    );
  }
  if (teamLocate.data.length === 0) return <Empty className="finance-payable-search-state" description="没有找到匹配的团" />;
  const columns: ColumnProps<FinancePrepaymentItinerary>[] = [
    {
      title: "线路标题",
      dataIndex: "itineraryTitle",
      render: (_, item) => (
        <Button type="text" className="finance-payable-link" onClick={() => onSelect(item.itineraryId)}>
          {item.itineraryTitle}
        </Button>
      ),
    },
    {
      title: "服务日期",
      dataIndex: "serviceDateFrom",
      width: 240,
      render: (_, item) => formatFinancePayableServiceDates(item.serviceDateFrom, item.serviceDateTo),
    },
  ];
  return (
    <div className="finance-payable-search-state">
      <p className="finance-payable-note">确认件里没有搜到，下面是按团编码找到的团。</p>
      <Table<FinancePrepaymentItinerary> rowKey="itineraryId" columns={columns} data={teamLocate.data} pagination={false} />
    </div>
  );
}

type SummaryState = ReturnType<typeof useFinancePayableListRuntime>["summary"];

/**
 * 选中团的应付：团头、结算主体行、未归口、团合计；有发起请款权限时结算主体行可以点「请款」，
 * 「按结算主体」右上有「给其他结算主体发起预付款」。团还没有确认件时交给 MissingWorkspace。
 */
function SettlementSummary({
  itineraryId,
  summary,
  canRequest,
}: {
  itineraryId: string;
  summary: SummaryState;
  canRequest: boolean;
}) {
  const [requestTarget, setRequestTarget] = useState<FinancePaymentRequestTarget | null>(null);
  if (summary.isPending) {
    return (
      <Card className="finance-payable-card">
        <Spin tip="正在加载应付…" />
      </Card>
    );
  }
  if (summary.isError) {
    if (isFinancePayableWorkspaceNotFound(summary.error)) {
      return <MissingWorkspace itineraryId={itineraryId} canRequest={canRequest} />;
    }
    return (
      <Card className="finance-payable-card">
        <Result
          status="error"
          title="应付汇总加载失败，请稍后重试。"
          extra={<Button type="primary" onClick={() => void summary.refetch()}>重试</Button>}
        />
      </Card>
    );
  }
  const data = summary.data;
  const onRequest = canRequest
    ? (row: { settlementSupplierId: string; settlementSupplierName: string }) =>
        setRequestTarget({
          itineraryId: data.itineraryId,
          itineraryTitle: data.workspace.itineraryTitle,
          settlementSupplierId: row.settlementSupplierId,
          settlementSupplierName: row.settlementSupplierName,
        })
    : null;
  return (
    <>
      <Card className="finance-payable-card" title={data.workspace.itineraryTitle}>
        <Descriptions
          column={2}
          data={[
            {
              label: "服务日期",
              value: formatFinancePayableServiceDates(data.workspace.serviceDateFrom, data.workspace.serviceDateTo),
            },
            { label: "确认件状态", value: <Tag>{financePayableWorkspaceStatusLabel(data.workspace.status)}</Tag> },
          ]}
        />
      </Card>
      <Card
        className="finance-payable-card"
        title="按结算主体"
        extra={
          <PrepaymentEntry
            canRequest={canRequest}
            itineraryId={data.itineraryId}
            itineraryTitle={data.workspace.itineraryTitle}
          />
        }
      >
        {isFinancePayableSummaryEmpty(data) ? (
          <Empty description="这个团的确认件里还没有协议和确认行。" />
        ) : (
          <SettlementTable rows={data.rows} onRequest={onRequest} />
        )}
        {data.prepaymentRows.length > 0 ? <PrepaymentRows rows={data.prepaymentRows} onRequest={onRequest} /> : null}
        {data.unassignedGroups.length > 0 ? <UnassignedGroups groups={data.unassignedGroups} /> : null}
        <Totals totals={data.totals} />
        <RequestTotals totals={data.totals} />
      </Card>
      {requestTarget ? (
        <FinancePaymentRequestFormModal target={requestTarget} onClose={() => setRequestTarget(null)} />
      ) : null}
    </>
  );
}

/**
 * 团还没有确认件：团名、服务日期取行程本身；这个团预付过的话列出预付款那几行和合计，只有应付清单页权限的人也看得到。
 * 有发起请款权限时多一个发起预付款的入口，因为有些结算主体要先付钱，确认件才做得出来。
 */
function MissingWorkspace({ itineraryId, canRequest }: { itineraryId: string; canRequest: boolean }) {
  const summary = useFinancePayablePrepaymentSummary(itineraryId);
  if (summary.isPending) {
    return (
      <Card className="finance-payable-card">
        <Spin tip="正在读取团…" />
      </Card>
    );
  }
  if (summary.isError) {
    return (
      <Card className="finance-payable-card">
        {isFinancePayableItineraryNotFound(summary.error) ? (
          <Empty description="找不到这个团，请重新搜索。" />
        ) : (
          <Result
            status="error"
            title="团的信息读取失败，请稍后重试。"
            extra={<Button type="primary" onClick={() => void summary.refetch()}>重试</Button>}
          />
        )}
      </Card>
    );
  }
  const data = summary.data;
  return (
    <>
      <Card className="finance-payable-card" title={data.itineraryTitle}>
        <Descriptions
          column={2}
          data={[
            { label: "服务日期", value: formatFinancePayableServiceDates(data.serviceDateFrom, data.serviceDateTo) },
            { label: "确认件状态", value: <Tag>还没有确认件</Tag> },
          ]}
        />
      </Card>
      <Card
        className="finance-payable-card"
        title="按结算主体"
        extra={
          <PrepaymentEntry canRequest={canRequest} itineraryId={data.itineraryId} itineraryTitle={data.itineraryTitle} />
        }
      >
        <Empty
          description={
            canRequest
              ? "这个团还没有确认件，暂无应付可看；可以先给结算主体发起预付款。"
              : "这个团还没有确认件，暂无应付可看。"
          }
        />
        {data.prepaymentRows.length > 0 ? (
          <>
            <PrepaymentRows rows={data.prepaymentRows} onRequest={null} />
            <RequestTotals totals={data.totals} />
          </>
        ) : null}
      </Card>
    </>
  );
}

/**
 * 「给其他结算主体发起预付款」按钮和它打开的预付款表单；没有发起请款权限时什么都不出。
 * 团有确认件、还没有确认件两处都用它，团名分别取自应付汇总和行程本身。
 */
function PrepaymentEntry({
  canRequest,
  itineraryId,
  itineraryTitle,
}: {
  canRequest: boolean;
  itineraryId: string;
  itineraryTitle: string;
}) {
  const [target, setTarget] = useState<FinancePrepaymentTarget | null>(null);
  if (!canRequest) return null;
  return (
    <>
      <Button onClick={() => setTarget({ itineraryId, itineraryTitle })}>{PREPAYMENT_ENTRY}</Button>
      {target ? <FinancePrepaymentFormModal target={target} onClose={() => setTarget(null)} /> : null}
    </>
  );
}

/**
 * 每个结算主体一行；没有签约基线时不显示基线相关的数，更不显示 0。
 * 基线在首次上传盖章件时冻结，所以已生效却没有基线的，是有基线之前就盖了章、之后没再上传盖章件或做增减的历史协议，不能写成未盖章。
 * 点行原地展开这张协议下的确认行，再点收起，可以同时展开多行。
 * onRequest 为空时（没有发起请款权限）不出「操作」列；点「请款」不展开这一行。
 */
function SettlementTable({
  rows,
  onRequest,
}: {
  rows: FinancePayableSettlementRow[];
  onRequest: ((row: FinancePayableSettlementRow) => void) | null;
}) {
  const columns: ColumnProps<FinancePayableSettlementRow>[] = [
    { title: "结算主体", dataIndex: "settlementSupplierName", width: 240 },
    { title: "协议编号", dataIndex: "agreementNo", width: 210 },
    {
      title: "协议状态",
      dataIndex: "agreementStatus",
      width: 100,
      render: (_, row) => financePayableAgreementStatusLabel(row.agreementStatus),
    },
    {
      title: "签约基线",
      dataIndex: "signedBaseline",
      width: 190,
      align: "right",
      render: (_, row) =>
        row.signedBaseline ? (
          <span className="finance-payable-money">
            {formatFinancePayableAmount(row.signedBaseline.baselineAmount)}
            {row.signedBaseline.backfilled ? <Tag size="small" color="orange">历史回填</Tag> : null}
          </span>
        ) : (
          <span className="finance-payable-muted">
            {row.agreementStatus === "effective" ? "历史协议，暂无基线" : "未盖章，无基线"}
          </span>
        ),
    },
    {
      title: "签后变化合计",
      dataIndex: "changeDeltaAmount",
      width: 140,
      align: "right",
      render: (_, row) =>
        row.signedBaseline ? (
          <Money value={row.changeDeltaAmount} />
        ) : (
          "—"
        ),
    },
    {
      title: "变更笔数",
      dataIndex: "changeBatchCount",
      width: 100,
      align: "right",
      render: (_, row) => (row.signedBaseline ? `${row.changeBatchCount} 笔` : "—"),
    },
    {
      title: "当前应付",
      dataIndex: "currentPayableAmount",
      width: 150,
      align: "right",
      render: (_, row) => <Money value={row.currentPayableAmount} />,
    },
    ...requestColumns<FinancePayableSettlementRow>(onRequest),
  ];
  return (
    <Table<FinancePayableSettlementRow>
      rowKey="agreementId"
      columns={columns}
      data={rows}
      pagination={false}
      scroll={{ x: onRequest ? 1970 : 1780 }}
      noDataElement={<Empty description="还没有生成确认协议" />}
      expandedRowRender={(row) => <AgreementLines row={row} />}
      expandProps={{ expandRowByClick: true }}
    />
  );
}

/** 能发起请款的那一行：结算主体两样，加上可请额度。 */
type RequestableRow = {
  settlementSupplierId: string;
  settlementSupplierName: string;
  requestedAmount: string;
  paidAmount: string | null;
  unpaidAmount: string | null;
  requestableAmount: string;
};

/**
 * 协议行和预付款行共用的四列：已请款、已付、未付、可请额度；onRequest 不为空（有发起请款权限）时再加「操作」列。
 * 可请额度为 0 或负数时「请款」灰掉，旁边写明原因；点「请款」不展开这一行。
 */
function requestColumns<Row extends RequestableRow>(onRequest: ((row: Row) => void) | null): ColumnProps<Row>[] {
  const money = (title: string, pick: (row: Row) => string | null): ColumnProps<Row> => ({
    title,
    width: 130,
    align: "right",
    render: (_, row) => <Money value={pick(row)} />,
  });
  return [
    money("已请款", (row) => row.requestedAmount),
    money("已付", (row) => row.paidAmount),
    money("未付", (row) => row.unpaidAmount),
    money("可请额度", (row) => row.requestableAmount),
    ...(onRequest
      ? [
          {
            title: "操作",
            width: 190,
            render: (_: unknown, row: Row) => {
              const blockedReason = financePayableRequestBlockedReason(row.requestableAmount);
              return (
                <div className="finance-payable-request-action">
                  <Button
                    type="text"
                    disabled={blockedReason !== null}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRequest(row);
                    }}
                  >
                    请款
                  </Button>
                  {blockedReason ? <span className="finance-payable-muted">{blockedReason}</span> : null}
                </div>
              );
            },
          },
        ]
      : []),
  ];
}

/**
 * 预付过、还没有确认协议的结算主体，各一行，当前应付为 0：付在确认件前面的钱也看得见。
 * 这些行的可请额度不会大于 0，「请款」总是灰的；再预付走「给其他结算主体发起预付款」。
 */
function PrepaymentRows({
  rows,
  onRequest,
}: {
  rows: FinancePayablePrepaymentRow[];
  onRequest: ((row: FinancePayablePrepaymentRow) => void) | null;
}) {
  const columns: ColumnProps<FinancePayablePrepaymentRow>[] = [
    { title: "结算主体", dataIndex: "settlementSupplierName" },
    {
      title: "当前应付",
      dataIndex: "currentPayableAmount",
      width: 150,
      align: "right",
      render: (_, row) => <Money value={row.currentPayableAmount} />,
    },
    ...requestColumns<FinancePayablePrepaymentRow>(onRequest),
  ];
  return (
    <section className="finance-payable-unassigned" data-testid="finance-payable-prepayments">
      <h3 className="finance-payable-section-title">预付款</h3>
      <p className="finance-payable-note">预付过、还没有确认协议的结算主体，当前应付为 0；确认协议做出来以后算进它那一行。</p>
      <Table<FinancePayablePrepaymentRow>
        rowKey="settlementSupplierId"
        columns={columns}
        data={rows}
        pagination={false}
        scroll={{ x: onRequest ? 1060 : 870 }}
      />
    </section>
  );
}

/**
 * 一张协议展开后的明细：逐行表格，底部是行合计。
 * 以协议当前应付为准，行合计对不上时多一行「未落到行的差额」，不在任何一边改数。
 */
function AgreementLines({ row }: { row: FinancePayableSettlementRow }) {
  return (
    <div className="finance-payable-lines" data-testid={`finance-payable-lines-${row.agreementId}`}>
      {row.lines.length > 0 ? (
        <LineTable lines={row.lines} />
      ) : (
        <p className="finance-payable-muted">这张协议下没有确认行</p>
      )}
      <Descriptions
        className="finance-payable-lines-footer"
        column={1}
        data={[
          { label: "行合计", value: <Money value={row.linesCurrentPayableTotal} /> },
          ...(hasFinancePayableUnattributedAmount(row.unattributedAmount)
            ? [{ label: "未落到行的差额", value: <Money value={row.unattributedAmount} /> }]
            : []),
        ]}
      />
    </div>
  );
}

/** 没有结算主体的那一组的行键；接口上它的结算主体 ID 为空。 */
const UNSPECIFIED_SUPPLIER_KEY = "unspecified";

/**
 * 还没生成协议的确认行，按行上的结算主体分组，顺序沿用接口给的（结算主体为空的一组在最后）。
 * 点组原地展开到每一行，再点收起，可以同时展开多组；组合计是组内各行原额之和，所以这一列叫「原额合计」。
 */
function UnassignedGroups({ groups }: { groups: FinancePayableUnassignedGroup[] }) {
  const groupKey = (group: FinancePayableUnassignedGroup) => group.settlementSupplierId ?? UNSPECIFIED_SUPPLIER_KEY;
  const columns: ColumnProps<FinancePayableUnassignedGroup>[] = [
    {
      title: "结算主体",
      dataIndex: "settlementSupplierName",
      render: (_, group) =>
        group.settlementSupplierId === null ? "未指定结算主体" : (group.settlementSupplierName ?? "—"),
    },
    { title: "笔数", dataIndex: "lineCount", width: 100, align: "right", render: (_, group) => `${group.lineCount} 笔` },
    {
      title: "原额合计",
      dataIndex: "amountTotal",
      width: 150,
      align: "right",
      render: (_, group) => <Money value={group.amountTotal} />,
    },
  ];
  return (
    <section className="finance-payable-unassigned" data-testid="finance-payable-unassigned">
      <h3 className="finance-payable-section-title">未归口</h3>
      <p className="finance-payable-note">还没生成协议的确认行，按行上的结算主体分组。</p>
      <Table<FinancePayableUnassignedGroup>
        rowKey={groupKey}
        columns={columns}
        data={groups}
        pagination={false}
        expandedRowRender={(group) => (
          <div className="finance-payable-lines" data-testid={`finance-payable-unassigned-lines-${groupKey(group)}`}>
            <LineTable lines={group.lines} />
          </div>
        )}
        expandProps={{ expandRowByClick: true }}
      />
    </section>
  );
}

/** 确认行逐行表格；没有真实服务对象的显示「—」，计价数带计价单位。 */
function LineTable({ lines }: { lines: FinancePayableLine[] }) {
  const columns: ColumnProps<FinancePayableLine>[] = [
    { title: "行号", dataIndex: "lineNo", width: 70 },
    { title: "类型", dataIndex: "resourceType", width: 80, render: (_, line) => <Tag>{line.resourceType}</Tag> },
    {
      title: "项目",
      dataIndex: "itemName",
      render: (_, line) => (
        <div className="finance-payable-stack">
          <span>{line.itemName}</span>
          {line.itemSpec ? <span className="finance-payable-muted">{line.itemSpec}</span> : null}
        </div>
      ),
    },
    {
      title: "真实服务对象",
      dataIndex: "serviceProviderName",
      render: (_, line) =>
        line.serviceProviderName ? (
          <div className="finance-payable-stack">
            <span>{line.serviceProviderName}</span>
            {line.serviceProviderType ? (
              <span className="finance-payable-muted">
                {financePayableServiceProviderTypeLabel(line.serviceProviderType)}
              </span>
            ) : null}
          </div>
        ) : (
          "—"
        ),
    },
    {
      title: "数量",
      dataIndex: "quantity",
      width: 90,
      align: "right",
      render: (_, line) => `${formatFinancePayableCount(line.quantity)} ${line.unit}`,
    },
    {
      title: "计价数",
      dataIndex: "billingCount",
      width: 90,
      align: "right",
      render: (_, line) => `${formatFinancePayableCount(line.billingCount)} ${line.specUnit}`,
    },
    {
      title: "单价",
      dataIndex: "unitPriceAmount",
      width: 120,
      align: "right",
      render: (_, line) => <Money value={line.unitPriceAmount} />,
    },
    { title: "原额", dataIndex: "amount", width: 130, align: "right", render: (_, line) => <Money value={line.amount} /> },
    {
      title: "调整",
      dataIndex: "adjustmentAmount",
      width: 120,
      align: "right",
      render: (_, line) => <Money value={line.adjustmentAmount} />,
    },
    {
      title: "当前应付",
      dataIndex: "currentPayableAmount",
      width: 130,
      align: "right",
      render: (_, line) => <Money value={line.currentPayableAmount} />,
    },
  ];
  return (
    <Table<FinancePayableLine>
      rowKey="lineId"
      size="small"
      columns={columns}
      data={lines}
      pagination={false}
      scroll={{ x: 1040 }}
    />
  );
}

/** 团合计：协议当前应付合计 ＋ 未归口合计 ＝ 团应付合计。 */
function Totals({ totals }: { totals: FinancePayableSettlementSummary["totals"] }) {
  return (
    <div className="finance-payable-totals" data-testid="finance-payable-totals">
      <Descriptions
        column={3}
        layout="vertical"
        data={[
          { label: "协议当前应付合计", value: <Money value={totals.currentPayableTotal} /> },
          { label: "未归口合计", value: <Money value={totals.unassignedTotal} /> },
          { label: "团应付合计", value: <strong><Money value={totals.itineraryPayableTotal} /></strong> },
        ]}
      />
    </div>
  );
}

/** 团合计里跟请款、付款有关的四个数：协议行和预付款行都加上；未付合计 ＝ 协议当前应付合计 − 已付合计。 */
function RequestTotals({ totals }: { totals: FinancePayableRequestTotals }) {
  return (
    <div className="finance-payable-totals" data-testid="finance-payable-request-totals">
      <Descriptions
        column={4}
        layout="vertical"
        data={[
          { label: "已请款合计", value: <Money value={totals.requestedTotal} /> },
          { label: "已付合计", value: <Money value={totals.paidTotal} /> },
          { label: "未付合计", value: <Money value={totals.unpaidTotal} /> },
          { label: "可请额度合计", value: <Money value={totals.requestableTotal} /> },
        ]}
      />
    </div>
  );
}

/** 金额单元格：¥ 加千分位两位小数，数字等宽。 */
function Money({ value }: { value: string | null }) {
  return <span className="finance-payable-money">{formatFinancePayableAmount(value)}</span>;
}
